/**
 * Global `keydown` handler for canvas editing: shortcuts defer to `canEdit` from
 * `useCanvasPermissions`. Registers once per mount with a ref to latest handlers from `CanvasPage`.
 */

import { useReactFlow, type Node } from "@xyflow/react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { CANVAS_KEYS } from "../canvas-controls.config";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { buildDetachUpdatesForSectionRemovals } from "../utils/sectionMembership";
import { useCanvasPermissions } from "./useCanvasPermissions";
import type { CopyPasteControls } from "./useCopyPaste";

export interface CanvasKeyboardShortcutsOptions {
  canvasId: string | undefined;
  lastCursorScreenPosRef: React.RefObject<{ x: number; y: number } | null>;
  canEdit: boolean;
  quickChat: {
    handleStartQuickChat: (pos: { x: number; y: number }) => void;
    handleStartQuickVoice: (pos: { x: number; y: number }) => void;
    composingState?: { mode?: string } | null;
    handleCancelComposing: () => void;
  };
  copyPaste: CopyPasteControls;
  undo: () => void;
  redo: () => void;
  canvas: {
    removeNodes: (ids: string[]) => void;
    removeEdges: (ids: string[]) => void;
    batchUpdateNodeParents: (
      updates: Array<{
        nodeId: string;
        parentId: null;
        position: { x: number; y: number };
      }>,
    ) => void;
    addNode: (node: any) => void;
  };
  isPlacingTextLabel: boolean;
  isPlacingTextNote: boolean;
  isPlacingDoc: boolean;
  setIsPlacingTextLabel: (v: boolean) => void;
  setIsPlacingTextNote: (v: boolean) => void;
  setIsPlacingDoc: (v: boolean) => void;
  handleToggleTextLabelMode: () => void;
  handleToggleTextNoteMode: () => void;
  handleToggleDocMode: () => void;
  cancelPlacingShape: () => void;
  cancelPlacingConnector: () => void;
  cancelPlacingSection: () => void;
  handleToggleSectionMode: () => void;
  setIsWidgetsMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  setIsLinkMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  setIsShapesMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  setIsNoteMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  isWidgetsMenuOpen: boolean;
  isLinkMenuOpen: boolean;
  lastPlacedShape: string;
  startPlacingShape: (shape: string) => void;
  onToggleExplorer: () => void;
  onInsertNewNote: () => void;
  onInsertNewPage: () => void;
  onInsertUploadFile: () => void;
}

export type CanvasKeyboardShortcutsOptionsInput = Omit<
  CanvasKeyboardShortcutsOptions,
  | "canvasId"
  | "canEdit"
  | "canvas"
  | "setIsWidgetsMenuOpen"
  | "setIsLinkMenuOpen"
  | "setIsShapesMenuOpen"
  | "setIsNoteMenuOpen"
  | "isWidgetsMenuOpen"
  | "isLinkMenuOpen"
>;

/**
 * Registers global keydown handler for canvas shortcuts (quick chat, voice,
 * placement modes, shapes, widgets, undo/redo, zoom, paste, copy/cut/duplicate, delete).
 * Uses a ref to read the latest options so the listener is registered once.
 * `canEdit` comes from `useCanvasPermissions()`; React Flow from `useReactFlow()`.
 */
export function useCanvasKeyboardShortcuts(
  options: CanvasKeyboardShortcutsOptionsInput,
) {
  const { canvasId } = useCanvasRoute();
  const ctx = useCanvas();
  const location = useLocation();
  const {
    setIsWidgetsMenuOpen,
    setIsLinkMenuOpen,
    setIsShapesMenuOpen,
    setIsNoteMenuOpen,
    isWidgetsMenuOpen,
    isLinkMenuOpen,
  } = useCanvasUi();
  const { canEdit } = useCanvasPermissions();
  const reactFlowInstance = useReactFlow();
  const reactFlowRef = useRef(reactFlowInstance);
  reactFlowRef.current = reactFlowInstance;

  const navRef = useRef({
    location,
    canvasId,
  });
  navRef.current = {
    location,
    canvasId,
  };
  const canvas = {
    removeNodes: ctx.removeNodes,
    removeEdges: ctx.removeEdges,
    batchUpdateNodeParents: ctx.batchUpdateNodeParents,
    addNode: ctx.addNode,
  };
  const optsRef = useRef<CanvasKeyboardShortcutsOptions>({
    ...options,
    canEdit,
    canvas,
    canvasId,
    setIsWidgetsMenuOpen,
    setIsLinkMenuOpen,
    setIsShapesMenuOpen,
    setIsNoteMenuOpen,
    isWidgetsMenuOpen,
    isLinkMenuOpen,
  });
  optsRef.current = {
    ...options,
    canEdit,
    canvas,
    canvasId,
    setIsWidgetsMenuOpen,
    setIsLinkMenuOpen,
    setIsShapesMenuOpen,
    setIsNoteMenuOpen,
    isWidgetsMenuOpen,
    isLinkMenuOpen,
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const opts = optsRef.current;
      const {
        canEdit,
        quickChat,
        copyPaste,
        undo,
        redo,
        canvas,
        lastCursorScreenPosRef,
      } = opts;
      const rf = reactFlowRef.current;

      const defaultPos = lastCursorScreenPosRef.current ?? {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      };

      if (
        event.key === CANVAS_KEYS.quickChat &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        quickChat.handleStartQuickChat(defaultPos);
        return;
      }

      if (
        event.key === CANVAS_KEYS.voiceInput &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        quickChat.handleStartQuickVoice(defaultPos);
        return;
      }

      if (
        event.key === CANVAS_KEYS.addTextLabel &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        if (opts.isPlacingTextNote) opts.setIsPlacingTextNote(false);
        if (opts.isPlacingDoc) opts.setIsPlacingDoc(false);
        opts.cancelPlacingSection();
        opts.setIsNoteMenuOpen(false);
        opts.handleToggleTextLabelMode();
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.addNote &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        opts.onInsertNewNote();
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.addLink &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        if (opts.isLinkMenuOpen) {
          opts.setIsLinkMenuOpen(false);
        } else {
          opts.setIsPlacingTextNote(false);
          opts.setIsPlacingTextLabel(false);
          opts.setIsPlacingDoc(false);
          opts.cancelPlacingShape();
          opts.cancelPlacingConnector();
          opts.cancelPlacingSection();
          opts.setIsShapesMenuOpen(false);
          opts.setIsNoteMenuOpen(false);
          opts.setIsWidgetsMenuOpen(false);
          opts.setIsLinkMenuOpen(true);
        }
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.uploadFile &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        canEdit
      ) {
        event.preventDefault();
        opts.onInsertUploadFile();
        return;
      }

      if (
        event.key === CANVAS_KEYS.addDoc &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        opts.onInsertNewPage();
        return;
      }

      if (
        event.key === CANVAS_KEYS.toggleExplorer &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        event.preventDefault();
        if (opts.isPlacingTextNote) opts.setIsPlacingTextNote(false);
        if (opts.isPlacingTextLabel) opts.setIsPlacingTextLabel(false);
        if (opts.isPlacingDoc) opts.setIsPlacingDoc(false);
        opts.cancelPlacingShape();
        opts.cancelPlacingConnector();
        opts.cancelPlacingSection();
        opts.setIsWidgetsMenuOpen(false);
        opts.setIsLinkMenuOpen(false);
        opts.setIsNoteMenuOpen(false);
        opts.onToggleExplorer();
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.addSection &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        canEdit
      ) {
        event.preventDefault();
        if (opts.isPlacingTextNote) opts.setIsPlacingTextNote(false);
        if (opts.isPlacingTextLabel) opts.setIsPlacingTextLabel(false);
        if (opts.isPlacingDoc) opts.setIsPlacingDoc(false);
        opts.cancelPlacingShape();
        opts.cancelPlacingConnector();
        opts.setIsWidgetsMenuOpen(false);
        opts.setIsLinkMenuOpen(false);
        opts.setIsShapesMenuOpen(false);
        opts.setIsNoteMenuOpen(false);
        opts.handleToggleSectionMode();
        return;
      }

      if (
        event.key === CANVAS_KEYS.addShapes &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        if (opts.isPlacingTextNote) opts.setIsPlacingTextNote(false);
        if (opts.isPlacingTextLabel) opts.setIsPlacingTextLabel(false);
        if (opts.isPlacingDoc) opts.setIsPlacingDoc(false);
        opts.cancelPlacingShape();
        opts.cancelPlacingConnector();
        opts.cancelPlacingSection();
        opts.setIsWidgetsMenuOpen(false);
        opts.setIsLinkMenuOpen(false);
        opts.setIsNoteMenuOpen(false);
        opts.setIsShapesMenuOpen((prev) => {
          if (!prev) {
            opts.startPlacingShape(opts.lastPlacedShape);
          }
          return !prev;
        });
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.addWidgets &&
        !event.ctrlKey &&
        !event.metaKey &&
        canEdit
      ) {
        event.preventDefault();
        if (opts.isWidgetsMenuOpen) {
          opts.setIsWidgetsMenuOpen(false);
        } else {
          opts.setIsPlacingTextNote(false);
          opts.setIsPlacingTextLabel(false);
          opts.setIsPlacingDoc(false);
          opts.cancelPlacingShape();
          opts.cancelPlacingConnector();
          opts.cancelPlacingSection();
          opts.setIsShapesMenuOpen(false);
          opts.setIsNoteMenuOpen(false);
          opts.setIsLinkMenuOpen(false);
          opts.setIsWidgetsMenuOpen(true);
        }
        return;
      }

      if (event.key === CANVAS_KEYS.cancel) {
        if (quickChat.composingState) {
          quickChat.handleCancelComposing();
          return;
        }
      }

      if (
        event.key === CANVAS_KEYS.undo &&
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        canEdit
      ) {
        event.preventDefault();
        undo();
        return;
      }

      if (
        event.key.toLowerCase() === CANVAS_KEYS.redo &&
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        canEdit
      ) {
        event.preventDefault();
        redo();
        return;
      }

      if (
        event.key === CANVAS_KEYS.zoomIn &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        rf.zoomIn({ duration: 200 });
        return;
      }
      if (
        event.key === CANVAS_KEYS.zoomOut &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        rf.zoomOut({ duration: 200 });
        return;
      }
      if (
        event.key === CANVAS_KEYS.zoomToFit &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        rf.fitView({ duration: 300 });
        return;
      }

      if (
        event.key === "v" &&
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        canEdit
      ) {
        if (copyPaste.canPaste) {
          event.preventDefault();
          copyPaste.paste();
          return;
        }
        // Don't preventDefault — let the native paste event fire so the paste
        // handler in useCopyPaste can access e.clipboardData (which includes
        // image files copied from Finder, unlike the async clipboard API).
        return;
      }

      if (event.key === "c" && (event.metaKey || event.ctrlKey)) {
        copyPaste.copy();
        return;
      }

      if (event.key === "d" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        copyPaste.duplicate();
        return;
      }

      if (event.key === "x" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        copyPaste.cut();
        return;
      }

      if (!canEdit) return;

      if (CANVAS_KEYS.delete.includes(event.key)) {
        const nodes = rf.getNodes();
        const edges = rf.getEdges();
        const selectedEdges = edges.filter((e) => e.selected);
        if (
          selectedEdges.length > 0 &&
          nodes.filter((n) => n.selected).length === 0
        ) {
          event.preventDefault();
          canvas.removeEdges(selectedEdges.map((e) => e.id));
          return;
        }
      }

      if (CANVAS_KEYS.delete.includes(event.key)) {
        const selectedNodes = rf
          .getNodes()
          .filter((node) => (node as Node).selected);

        if (selectedNodes.length > 0) {
          event.preventDefault();
          const nodeIds = selectedNodes.map((n) => n.id);
          const nodes = rf.getNodes();
          const detach = buildDetachUpdatesForSectionRemovals(
            nodeIds,
            nodes,
            rf.getInternalNode,
          );
          if (detach.length > 0) {
            canvas.batchUpdateNodeParents(detach);
          }
          canvas.removeNodes(nodeIds);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
}
